import * as React from 'react'
import {format} from 'date-fns'

import {type PolyfilledEvent} from '@frontrow/console/src/polyfills'
// Components
import {Text, H2} from '@frontrow/console/src/components/typography'
import {Button} from '@frontrow/console/src/components/button'
import Input from '@frontrow/console/src/entities/components/base/input'
import {Link} from '@frontrow/console/src/components/ui/link'
import {Card} from '@frontrow/console/src/components/ui/card'
import {type IdentityProviderT} from '@frontrow/console/src/entities/helpers/identity-provider'
import {type StarStatus} from '@frontrow/console/src/entities/models/school'
import {type BasicSchoolT, type DetailedSchoolT} from '@frontrow/console/src/models/school'
// Helpers
import {type AddressT, type WeakAddressT} from '@frontrow/console/src/helpers/addresses'
import {extractAddress} from '@frontrow/console/src/helpers/addresses'
import {FIELD_GRID} from '@frontrow/console/src/lib/form-styles'
// Components
import FormField from '@frontrow/console/src/components/common/form-field'
import AddressForm from '@frontrow/console/src/components/common/address-form'
import LabelledInput from '@frontrow/console/src/components/common/labelled-input'
import {RenaissanceConfigurationFormFields} from '@frontrow/console/src/components/common/renaissance-configuration-form-fields'
import IDPConfigurationFormFields from '@frontrow/console/src/components/common/idp-configuration-form-fields'

type Props = {
  //School Details
  school: BasicSchoolT
  schoolToUpdate: DetailedSchoolT
  //Form features
  canAddFakeDistrict: boolean
  canUpdateCrmId: boolean
  shouldAddFakeDistrict: boolean
  shouldMuteLicenseUtilizationNotifications: boolean
  shouldDeleteCleverAndClasslinkAssociations: boolean
  syncPaused: boolean
  selectedIdp: IdentityProviderT | undefined | null
  crmManaged: boolean
  //Renaissance Client
  renaissanceSchoolClientClientId: string | undefined | null
  renaissanceSchoolClientRpid: string | undefined | null
  renaissanceSchoolClientBaseUrl: string | undefined | null
  renaissanceSchoolClientPlatform: string
  renaissanceSchoolClientStarStatus: StarStatus
  //Form validations
  needsStarIntegrationFields: boolean
  needsRenaissanceIdPFields: boolean
  renaissanceFieldsRequired: boolean
  showStarSyncFreshlyEnabledNotification: boolean
  //Submit
  // Null when the form can be submitted; otherwise the first reason it cannot,
  // which is also what disables the button.
  submitDisabledReason: string | null
  onSubmit: (e: PolyfilledEvent) => void
  //Form Events
  onChangeAddress: (address: AddressT) => void
  onChangeBaseUrl: (baseUrl: string) => void
  onChangeClassLinkSourceId: (classLinkSourceId: string) => void
  onChangeCleverSchoolId: (cleverSchoolId: string) => void
  onChangeClientId: (clientId: string) => void
  onChangeDistrictId: (districtId: string) => void
  onChangeIdp: (idp?: IdentityProviderT | null) => void
  onChangeLatitude: (latitude: string) => void
  onChangeLongitude: (longitude: string) => void
  onChangeRenaissancePlatform: (e: PolyfilledEvent) => void
  onChangeRpIdentifier: (rpIdentifier: string) => void
  onChangeRpid: (rpid: string) => void
  onChangeSchoolName: (name: string) => void
  onChangeCrmId: (crmId: string) => void
  onChangeSchoolSisId: (sisId: string) => void
  onToggleShouldAddFakeDistrict: () => void
  onToggleShouldMuteLicenseUtilizationNotifications: () => void
  onUpdateStarStatus: (starStatus: StarStatus) => void
  onTogglePauseSync: () => void
}

// Field spans in the twelve-column section grid. A HALF_WIDE field takes half a
// row on a wide window and the full row below `lg`, because half of a narrow
// window is not enough for a 36-character UUID.
const HALF_WIDE = 'mb-0 pr-0 sm:col-span-12 lg:col-span-6'
const FULL = 'mb-0 pr-0 sm:col-span-12'

const SECTION_LINKS = [
  {href: '#school-information', label: 'School Information'},
  {href: '#renaissance-configuration', label: 'Renaissance Configuration'},
  {href: '#identity-provider', label: 'Identity Provider'}
]

export default function EditSchoolForm(props: Props): React.ReactElement {
  const {
    school,
    schoolToUpdate,

    canAddFakeDistrict,
    canUpdateCrmId,
    shouldAddFakeDistrict,
    shouldMuteLicenseUtilizationNotifications,
    shouldDeleteCleverAndClasslinkAssociations,
    crmManaged,

    renaissanceSchoolClientClientId,
    renaissanceSchoolClientRpid,
    renaissanceSchoolClientBaseUrl,
    renaissanceSchoolClientPlatform,
    renaissanceSchoolClientStarStatus,

    needsStarIntegrationFields,
    needsRenaissanceIdPFields,
    renaissanceFieldsRequired,
    showStarSyncFreshlyEnabledNotification,
    syncPaused,
    selectedIdp,

    submitDisabledReason,
    onSubmit,

    onChangeAddress,
    onChangeBaseUrl,
    onChangeClassLinkSourceId,
    onChangeCleverSchoolId,
    onChangeClientId,
    onChangeDistrictId,
    onChangeIdp,
    onChangeLatitude,
    onChangeLongitude,
    onChangeRenaissancePlatform,
    onChangeRpIdentifier,
    onChangeRpid,
    onChangeSchoolName,
    onChangeCrmId,
    onChangeSchoolSisId,
    onToggleShouldAddFakeDistrict,
    onToggleShouldMuteLicenseUtilizationNotifications,
    onUpdateStarStatus,
    onTogglePauseSync
  } = props

  const {
    name,
    districtId,
    sisId,
    latLong,
    classLinkSourceId,
    cleverSchoolId,
    renaissanceRpIdentifier,
    crmId
  } = school
  const latitude = latLong ? latLong.latitude : NaN
  const longitude = latLong ? latLong.longitude : NaN

  const placeholders = {name: 'Name is required'}

  const fullAddress = extractAddress(school as WeakAddressT)
  const lastMutedAt =
    schoolToUpdate.licenseUtilizationNotificationsMutedAt !== null &&
    schoolToUpdate.licenseUtilizationNotificationsMutedAt !== undefined ? (
      <p className="mt-1 text-xs italic text-muted-text">
        Last muted{' '}
        {format(schoolToUpdate.licenseUtilizationNotificationsMutedAt, 'MMMM do yyyy h:mm:ss aaa')}
      </p>
    ) : null

  const muteNotifications = (
    <React.Fragment>
      <hr className="border-border sm:col-span-12" />
      <div className={FULL}>
        <LabelledInput label="Mute License Utilization Notifications">
          <input
            type="checkbox"
            checked={shouldMuteLicenseUtilizationNotifications}
            onChange={onToggleShouldMuteLicenseUtilizationNotifications}
          />
        </LabelledInput>
        {lastMutedAt}
      </div>
    </React.Fragment>
  )

  const coordinates = (
    <div className="grid grid-cols-1 gap-4">
      <FormField label="Latitude" htmlFor="school-latitude" wrapperStyle="mb-0 pr-0">
        <Input
          id="school-latitude"
          value={isNaN(latitude as number) ? '' : (latitude as number)}
          type="number"
          disabled={crmManaged}
          onChange={onChangeLatitude}
          dataTest="latitude-input"
        />
      </FormField>
      <FormField label="Longitude" htmlFor="school-longitude" wrapperStyle="mb-0 pr-0">
        <Input
          id="school-longitude"
          value={isNaN(longitude as number) ? '' : (longitude as number)}
          type="number"
          disabled={crmManaged}
          onChange={onChangeLongitude}
          dataTest="longitude-input"
        />
      </FormField>
    </div>
  )

  const showRenaissanceIDPError =
    selectedIdp === 'renaissance' &&
    (needsRenaissanceIdPFields || renaissanceSchoolClientPlatform !== 'renaissance_growth_platform')

  const submitDisabled = submitDisabledReason !== null

  const disabledHint = submitDisabled ? (
    <Text style="body-2" addClass="text-xs text-muted-text">
      {submitDisabledReason}
    </Text>
  ) : null

  return (
    <form onSubmit={onSubmit} action="" className="space-y-6">
      <nav className="flex flex-wrap items-center gap-1" aria-label="Jump to section">
        <span className="mr-1 inline-block text-sm font-medium leading-none text-muted-text">
          Jump to:
        </span>
        {SECTION_LINKS.map(({href, label}) => (
          <Link key={href} href={href} addClass="rounded px-2 py-1 text-sm hover:bg-surface-hover">
            {label}
          </Link>
        ))}
      </nav>

      <Card htmlElement="section" id="school-information" addClass="scroll-mt-6">
        <div className="mb-4 flex items-baseline justify-between gap-4">
          <H2 addClass="my-0">School Information</H2>
          <Text style="body-2" addClass="shrink-0 text-xs text-muted-text">
            * Required
          </Text>
        </div>

        <div className={FIELD_GRID}>
          <FormField wrapperStyle={HALF_WIDE} label="School Name" htmlFor="school-name" required>
            <Input
              id="school-name"
              disabled={crmManaged}
              value={name}
              type="text"
              required
              placeholder={placeholders.name}
              onChange={onChangeSchoolName}
              dataTest="school-name-input"
            />
          </FormField>

          <FormField wrapperStyle={HALF_WIDE} label="School CRM ID" htmlFor="school-crm-id">
            <Input
              id="school-crm-id"
              className="font-mono"
              disabled={!canUpdateCrmId}
              value={crmId || ''}
              type="text"
              onChange={onChangeCrmId}
            />
          </FormField>

          <div className={FULL}>
            <AddressForm
              disabled={crmManaged}
              isNewAddress={false}
              address={fullAddress}
              onChangeAddress={onChangeAddress}
              aside={coordinates}
            />
          </div>

          <hr className="border-border sm:col-span-12" />

          {/* A fieldset, so the two options read as one choice and the group
              carries a name; a bare <label> would have no control to point at. */}
          <fieldset className={FULL}>
            <legend className="text-sm font-medium leading-none">District*</legend>
            <div className="mt-2 flex flex-col gap-2">
              <LabelledInput label="Create a fake district automatically">
                <input
                  type="radio"
                  name="district-source"
                  disabled={!canAddFakeDistrict}
                  checked={shouldAddFakeDistrict}
                  onChange={onToggleShouldAddFakeDistrict}
                />
              </LabelledInput>
              <div className="flex flex-wrap items-center gap-3">
                <LabelledInput label="Use a real district ID">
                  <input
                    type="radio"
                    name="district-source"
                    checked={!shouldAddFakeDistrict}
                    onChange={onToggleShouldAddFakeDistrict}
                  />
                </LabelledInput>
                <Input
                  className="w-32 font-mono"
                  required={!shouldAddFakeDistrict}
                  disabled={shouldAddFakeDistrict}
                  value={isNaN(districtId) ? '' : districtId}
                  type="number"
                  onChange={onChangeDistrictId}
                  dataTest="district-id-input"
                />
              </div>
            </div>
          </fieldset>

          <FormField wrapperStyle={HALF_WIDE} label="SIS ID" htmlFor="school-sis-id">
            <Input
              id="school-sis-id"
              className="font-mono"
              value={sisId || ''}
              type="text"
              onChange={onChangeSchoolSisId}
            />
          </FormField>

          {muteNotifications}
        </div>
      </Card>

      <RenaissanceConfigurationFormFields
        renaissanceFieldsRequired={renaissanceFieldsRequired}
        renaissanceSchoolClientClientId={renaissanceSchoolClientClientId}
        onChangeClientId={onChangeClientId}
        renaissanceSchoolClientRpid={renaissanceSchoolClientRpid}
        onChangeRpid={onChangeRpid}
        renaissanceSchoolClientBaseUrl={renaissanceSchoolClientBaseUrl}
        onChangeBaseUrl={onChangeBaseUrl}
        renaissanceRpIdentifier={renaissanceRpIdentifier}
        onChangeRpIdentifier={onChangeRpIdentifier}
        renaissanceSchoolClientStarStatus={renaissanceSchoolClientStarStatus}
        onUpdateStarStatus={onUpdateStarStatus}
        showStarSyncFreshlyEnabledNotification={showStarSyncFreshlyEnabledNotification}
        renaissanceSchoolClientPlatform={renaissanceSchoolClientPlatform}
        onChangeRenaissancePlatform={onChangeRenaissancePlatform}
        needsStarIntegrationFields={needsStarIntegrationFields}
        showRenaissanceIDPError={showRenaissanceIDPError}
      />

      <IDPConfigurationFormFields
        shouldShowDeleteCleverAndClasslinkAssociationsNotification={
          shouldDeleteCleverAndClasslinkAssociations
        }
        cleverSchoolId={cleverSchoolId}
        syncPaused={syncPaused}
        classLinkSourceId={classLinkSourceId}
        onChangeIdp={onChangeIdp}
        onTogglePauseSync={onTogglePauseSync}
        onChangeClassLinkSourceId={onChangeClassLinkSourceId}
        onChangeCleverSchoolId={onChangeCleverSchoolId}
        identityProvider={selectedIdp}
        showRenaissanceIDPError={showRenaissanceIDPError}
        needsStarIntegrationFields={needsStarIntegrationFields}
      />

      <div className="sticky bottom-0 z-10 flex flex-wrap items-center gap-4 border-t border-border bg-background py-4 shadow-[0_-4px_8px_-6px_rgba(0,0,0,0.25)]">
        <Button type="submit" disabled={submitDisabled} dataTest="update-school-btn">
          Update School
        </Button>
        {disabledHint}
      </div>
    </form>
  )
}
